import { array } from '@ember/helper';
import { on } from '@ember/modifier';
import { action } from '@ember/object';
import type Owner from '@ember/owner';
import { service } from '@ember/service';
import { tracked } from '@glimmer/tracking';

import type { Service } from 'webapp-types';
import { Direction, PopupEvent } from 'webapp/enums/enums';
import { isAlive } from 'webapp/utils/common';
import { getRelativeTime } from 'webapp/utils/number';

import highlightResult from 'webapp/helpers/highlight-result';
import joinClasses from 'webapp/helpers/join-classes';
import didInsert from 'webapp/modifiers/did-insert';
import visibleCheckItem from 'webapp/modifiers/visible-check-item';
import CommonConditionalTooltipComponent from 'webapp/components/common/conditional-tooltip';
import CommonUiIconComponent from 'webapp/components/common/ui-icon';
import type { Args as BaseArgs } from 'webapp/components/metric-library/grid-item-base';
import MetricLibraryGridItemBaseComponent from 'webapp/components/metric-library/grid-item-base';
import MetricLibraryPopupsPopupMetricComponent from 'webapp/components/metric-library/popups/popup-metric';
import SourceConnectButtonComponent from 'webapp/components/source/connect/button';
import SubmitButtonComponent from 'webapp/components/submit-button';
import ToolTipComponent from 'webapp/components/tool-tip';
import VerificationBadgeComponent from 'webapp/components/verification/badge';

import type { BatchSelect, Metric } from 'webapp/models-custom';
import Goal from 'webapp/models-custom/goal';
import HorizonMetric from 'webapp/models-custom/horizon-metric';

export const enum Size {
	NORMAL = 'normal',
	SMALL = 'small',
}

export interface Args extends BaseArgs {
	size?: Size;
	hideActions?: boolean;
	isVisibleOnScreen?: boolean;
	canSelect?: boolean;
	batchSelect?: BatchSelect<Metric> | null;
	onSelectMetric?: (metric: Metric) => void;
	onConnectMetric?: (metric?: Metric) => void;
}

export default class MetricLibraryGridItemComponent extends MetricLibraryGridItemBaseComponent<Args> {
	@service('popup') declare popupService: Service.Popup;
	@service('connect') declare connectService: Service.Connect;
	@service('flow/tour/onboarding')
	declare flowTourOnboardingService: Service.Flow.Tour.Onboarding;
	@service('model-manager/source-types')
	declare mmSourceTypesService: Service.ModelManager.SourceTypes;
	@service('model-manager/users') declare mmUsersService: Service.ModelManager.Users;

	@tracked isPopupOpen = false;
	@tracked isVisibleOnScreen;

	constructor(owner: Owner, args: Args) {
		super(owner, args);

		this.isVisibleOnScreen = this.args.isVisibleOnScreen ?? false;
	}

	node: HTMLElement | null = null;

	override get model(): Metric {
		return this.args.model;
	}

	override get metricName() {
		let name = this.model.name;
		if (this.model.isDeprecated) {
			name += ' (deprecated)';
		}
		return name;
	}

	get description() {
		const { rootModel, description } = this.model;

		// A real description always wins; creation info is only a stand-in for the
		// built/calculated metrics that don't have one.
		if (description) {
			return description;
		}

		if (rootModel && !(rootModel instanceof Goal)) {
			const userRecord =
				rootModel instanceof HorizonMetric ? rootModel.createdByUser : rootModel.createdBy;

			if (userRecord) {
				const relativeTime = getRelativeTime(rootModel.created);
				const metricType = this.model.isTypeBuilt ? 'Built' : 'Calculated';

				return `${metricType} metric created by ${userRecord.name}, ${relativeTime}`;
			}
		}

		return description;
	}

	get hideActions() {
		return this.args.hideActions || (this.isConnected && this.isDisabled);
	}

	get isConnected() {
		return !!this.source?.id;
	}

	get connectButtonText() {
		return this.isSmallSize ? '' : 'Connect';
	}

	get isFavoriteShown() {
		return this.model.isFavorite && this.isNormalSize;
	}

	get isSelected(): boolean {
		const { batchSelect, model } = this.args;
		return !!batchSelect?.isItemSelected(model);
	}

	get size(): Size {
		return this.args.size || Size.NORMAL;
	}

	get isNormalSize(): boolean {
		return this.size === Size.NORMAL;
	}

	get isSmallSize(): boolean {
		return this.size === Size.SMALL;
	}

	get isActionBtnShown(): boolean {
		return !this.flowTourOnboardingService.isBannerShown;
	}

	get sizeClass(): string {
		return 'card--' + this.size + '-metric-type';
	}

	get isSelectable() {
		return !!this.args.canSelect && !this.isDisabled;
	}

	get isDataset() {
		return this.source?.isDataset && this.args.model.isTypeCustomQuery;
	}

	@action
	openContext(e: PointerEvent) {
		e.stopPropagation();
		const attachTo = this.node?.querySelector<HTMLElement>('.context');

		if (this.isPopupOpen || !attachTo) {
			return;
		}

		// Clicking on kebab icon of another metric box closes popup on previous and
		// open new one on current clicked.
		let beforeOpen;
		if (this.popupService.isOpen(MetricLibraryPopupsPopupMetricComponent)) {
			beforeOpen = this.popupService.activePopup?.close();
		} else {
			beforeOpen = Promise.resolve();
		}

		const openPopupFn = () => {
			const popup = this.popupService.createContext(
				MetricLibraryPopupsPopupMetricComponent,
				attachTo,
				{
					popupClass: 'source-metric-popup',
					directions: [Direction.UP, Direction.DOWN, Direction.RIGHT, Direction.LEFT],
					allowStacked: true, // for bubble popup
				},
			);

			popup.open({
				model: this.model,
				showDragNDrop: false,
				showActions: !this.args.hideActions,
				onConnect: () => {
					this.connectMetric();
				},
				trackingOrigin: 'library',
			});

			this.isPopupOpen = true;
			popup.on(PopupEvent.CLOSED, this, () => {
				if (isAlive(this)) {
					this.isPopupOpen = false;
				}
			});

			return popup;
		};

		return beforeOpen?.then(openPopupFn);
	}

	@action
	selectMetric(e: PointerEvent) {
		const targetElement = e.target as HTMLElement;
		if (!this.isSelectable || targetElement.closest('.card__action')) {
			return;
		}

		const { model, batchSelect } = this.args;
		batchSelect?.changeSelection(model, !this.isSelected);

		if (this.args.onSelectMetric) {
			this.args.onSelectMetric(this.args.model);
		}
	}

	@action
	connectMetric() {
		if (this.accountService.handleDemo()) {
			return;
		}

		if (this.args.onConnectMetric) {
			this.args.onConnectMetric(this.model);
		} else {
			void this.connectService.connectSourceFromIdOrKey(this.sourceType.key, {
				origin: this.origin,
			});
		}
	}

	@action
	visibleChanged(isVisible: boolean): void {
		this.isVisibleOnScreen = isVisible;
	}

	@action
	setupView(node: HTMLElement): void {
		this.node = node;
	}
	<template>
		<li
			class={{joinClasses
				(array
					this.model.animationClass
					"card"
					"flex-item"
					"filter-sort-item"
					this.sizeClass
					(if this.isSelected "card--selected")
					(if this.isSelectable "card--selectable")
					(if this.isPopupOpen " card--open")
				)
			}}
			role="button"
			data-id={{this.model.uniqueId}}
			{{didInsert this.setupView}}
			{{on "click" this.selectMetric}}
		>

			{{#if this.isVisibleOnScreen}}

				<div class="card__wrapper">
					<div class="card__header">
						<div class="card__source">
							<img src={{this.sourceAvatar}} alt={{this.sourceTitle}} />
							<div class="card__source-name">
								<div class="overflowable">{{this.sourceTitle}}</div>
							</div>
						</div>
						<div class="card__metric">
							<div class="card__metric-name">
								<CommonConditionalTooltipComponent
									@text={{this.metricName}}
									@maxWidth={{250}}
								>
									{{highlightResult
										text=this.metricName
										match=this.model.activeFilter
									}}
								</CommonConditionalTooltipComponent>
							</div>
							{{#if this.isDataset}}
								<CommonUiIconComponent @name="Table" @size="sm" />
							{{/if}}
							<VerificationBadgeComponent
								@verification={{@model.verificationInfo}}
								@entityLabel="metric"
								@size="sm"
							/>
							{{#if this.isFavoriteShown}}
								<CommonUiIconComponent @name="StarFill" @size="sm" />
							{{/if}}
							{{#if @model.isNew}}
								<div class="new-feature-tag">New</div>
							{{/if}}
						</div>
					</div>
					<div class="card__content-wrapper">
						<div class="card__content">
							{{#if this.isNormalSize}}
								<div class="card__description">
									{{highlightResult
										text=this.description
										match=this.model.activeFilter
									}}
								</div>
							{{/if}}

							<div class="card__actions">
								{{#unless this.hideActions}}
									{{#if this.isConnected}}
										{{#if this.isNormalSize}}
											{{#if this.canAddToHome}}
												<SubmitButtonComponent
													@text={{this.buttonText}}
													@extraClass="card__action"
													@icon="Plus"
													@submitInProgress={{this.isLoading}}
													@onTrigger={{this.createNew}}
												/>
											{{/if}}
										{{/if}}
									{{else}}
										<SourceConnectButtonComponent
											@sourceType={{this.sourceType}}
											@onConnect={{this.connectMetric}}
											@text={{this.connectButtonText}}
											@theme="green"
										/>
									{{/if}}
								{{/unless}}

								{{#if this.isActionBtnShown}}
									<a
										class={{joinClasses
											(array
												"context"
												"card__action"
												(if this.isPopupOpen " context--open")
											)
										}}
										{{on "click" this.openContext}}
									>
										<CommonUiIconComponent @name="Ellipsis" @size="sm" />
										{{#unless this.isSmallSize}}
											<span class="context__text">More</span>
										{{/unless}}
									</a>
								{{/if}}
							</div>
						</div>
					</div>
					{{#unless @isStatusHidden}}
						{{#if this.isDisabled}}
							{{#if this.source.isStatusActionable}}
								<div class="card__status card__status--attention">
									<CommonUiIconComponent @name="AlertFill" @size="sm" />
									<ToolTipComponent
										@text={{this.source.statusObject.statusTitleAndDescriptionWithAction}}
										@interactive={{true}}
										@maxWidth={{235}}
									/>
								</div>
							{{else}}
								<CommonUiIconComponent
									@name="Lock"
									@size="sm"
									@class="card__status"
								/>
							{{/if}}
						{{/if}}
					{{/unless}}
				</div>
			{{else}}
				<div class="info" {{visibleCheckItem this.visibleChanged false}}>
					<div class="anti-stretch">&nbsp;</div>
				</div>
			{{/if}}
		</li>
	</template>
}
